import { Divider, IconButton, Tooltip, Typography } from '@material-ui/core'
import classNames from 'classnames'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import CostPriceExplanationModal from 'components/ProjectPage/BillOfMaterials/RowDetails/CostPriceExplanationModal'
import { useWorkingStepFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/useWorkingStepFilter'
import { TargetPriceGraphData } from 'features/BomItemDetailsDrawer/useBomItemDetails'
import { MoneyString } from 'model/Money'
import { MoneyPerQuantityString } from 'model/MoneyPerQuantity'
import { QuantityString, TimeString } from 'model/Quantity'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ManuallySetAdditionalCostsArgs,
  ManuallySetProductionTimeArgs,
  PriceRowDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { APICall, ErrorMessage } from 'store/Application/appActions'
import store from 'store/configureStore'
import { StoreUtils } from 'store/storeUtils'
import { ReactComponent as InfoOverIcon } from '_icons/info_over-sm.svg'
import { ReactComponent as DownloadIcon } from '_icons/project-menu/download.svg'
import { useFinancialDetailsFormStyles } from './FinancialDetailsForm.styles'
import { AdditionalProdCostsInput } from './Inputs/AdditionalProdCostsInput'
import { ProdTimeInput } from './Inputs/ProdTimeInput'

type Props = {
  bomItemId: string
  priceDetails: PriceRowDto[]
  summarizedPriceDetails: TargetPriceGraphData[]
  financialUnit: 'perUnit' | 'total'
  handleResetProductionTime: (
    rowId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  handleSaveProductionTime: (
    rowId: string,
    args: ManuallySetProductionTimeArgs
  ) => Promise<void>
  handleResetAdditionalCosts: (
    rowId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  handleSaveAdditionalCostPrice: (
    rowId: string,
    args: ManuallySetAdditionalCostsArgs
  ) => Promise<void>
  loading?: Record<string, boolean>
}

const downloadCostDriverCalculators = (
  rowId: string,
  primaryWorkingStep: WorkingStepType,
  secondaryWorkingStep: WorkingStepType
) => {
  const { partyId, projectId } = StoreUtils.GetContext()

  store.dispatch(
    APICall({
      label: `downloadCostDriverCalculators_${rowId}_${primaryWorkingStep}_${secondaryWorkingStep}`,
      configuration: {
        method: 'GET',
        url: `api/parties/${partyId}/projects/${projectId}/items/${rowId}/workingSteps/costDriverCalculators?primaryWorkingStep=${primaryWorkingStep}&secondaryWorkingStep=${secondaryWorkingStep}`,
        isFileDownload: true,
        onProgress: (progress) =>
          console.info('downloadCostDriverCalculators -> progress:', progress),
        onError: (message) => {
          ErrorMessage('cost drivers', message)
        },
      },
    })
  )
}

const VALUE_LABEL_WIDTH = '100%'

export const FinancialDetailsForm = (props: Props) => {
  const classes = useFinancialDetailsFormStyles()
  const { t } = useTranslation()
  const [activeCostPriceExplanation, setActiveCostPriceExplanation] =
    useState(null)

  const { getLocalizedWorkingStep } = useLocalizedWorkingStepLabel()

  const { workingStepFilter } = useWorkingStepFilter()

  if (!props.priceDetails) {
    return null
  }

  const materialRow = props.priceDetails.find(
    (x) => x?.isWorkingStep === false && x.isTotalRow === false
  )

  const workingStepRows = props.priceDetails.filter(
    (x) => x?.isWorkingStep === true && x.isTotalRow === false
  )

  const totalRow = props.priceDetails.find((x) => x.isTotalRow)

  return (
    <ul className={classes.root}>
      {activeCostPriceExplanation != null && (
        <CostPriceExplanationModal
          isActive={true}
          rowId={props.bomItemId}
          onClose={() => setActiveCostPriceExplanation(null)}
          workingStepType={activeCostPriceExplanation}
        />
      )}
      {(materialRow || workingStepRows?.length > 0) && (
        <div
          className={classes.values}
          style={{
            textAlign: 'right',
            alignItems: 'stretch',
          }}
        >
          <LocalizedTypography
            translationKey="project:prod-time"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            prod time
          </LocalizedTypography>
          <LocalizedTypography
            translationKey="project:costs-prod"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            costs [prod time]
          </LocalizedTypography>
          <LocalizedTypography
            translationKey="project:costs-additional"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            costs [additional]
          </LocalizedTypography>
          <LocalizedTypography
            translationKey="project:costs-total"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            costs [total]
          </LocalizedTypography>
          <LocalizedTypography
            translationKey="common:surcharge"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            surcharge
          </LocalizedTypography>
          <LocalizedTypography
            translationKey="project:discount"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            discount
          </LocalizedTypography>

          <LocalizedTypography
            translationKey="project:sales-price"
            variant="caption"
            style={{ width: VALUE_LABEL_WIDTH }}
          >
            sales price
          </LocalizedTypography>
        </div>
      )}

      {materialRow && (
        <li className={classes.row}>
          <div className={classes.header}>
            <div className={classes.resourceDescription}>
              <Typography variant="body1">material</Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setActiveCostPriceExplanation(
                    materialRow.workingStep.primaryWorkingStep
                  )
                }
              >
                <InfoOverIcon width="21px" />
              </IconButton>
            </div>
          </div>
          <div className={classes.values}>
            <Typography variant="body2" style={{ textAlign: 'right' }}>
              -
            </Typography>
            <Typography variant="body2" style={{ textAlign: 'right' }}>
              -
            </Typography>
            <Tooltip
              title={
                <>{`${t('project:costs-additional')}: ${MoneyString(
                  materialRow?.additionalProductionCosts,
                  10
                )}`}</>
              }
            >
              <span style={{ textAlign: 'right' }}>
                <ValueLabel
                  valueLabelRootStyles={{
                    width: VALUE_LABEL_WIDTH,
                  }}
                  valueProps={{
                    variant: 'body2',
                  }}
                  value={MoneyString(materialRow?.additionalProductionCosts)}
                />
              </span>
            </Tooltip>
            <Tooltip
              title={
                <>{`${t('project:costs-total')}: ${MoneyString(
                  materialRow?.costPrice,
                  10
                )}`}</>
              }
            >
              <span style={{ textAlign: 'right' }}>
                <ValueLabel
                  valueLabelRootStyles={{
                    width: VALUE_LABEL_WIDTH,
                  }}
                  valueProps={{
                    variant: 'body2',
                  }}
                  value={MoneyString(materialRow?.costPrice)}
                />
              </span>
            </Tooltip>

            <Tooltip
              title={
                <>{`${t('common:surcharge')}: ${MoneyString(
                  materialRow?.surchargeValue,
                  10
                )}`}</>
              }
            >
              <span style={{ textAlign: 'right' }}>
                <ValueLabel
                  valueLabelRootStyles={{
                    width: VALUE_LABEL_WIDTH,
                  }}
                  valueProps={{
                    variant: 'body2',
                  }}
                  value={MoneyString(materialRow?.surchargeValue)}
                />
              </span>
            </Tooltip>

            <Tooltip
              title={
                <>{`${t('common:discount')}: ${MoneyString(
                  materialRow?.discountValue,
                  10
                )}`}</>
              }
            >
              <span style={{ textAlign: 'right' }}>
                <ValueLabel
                  valueLabelRootStyles={{
                    width: VALUE_LABEL_WIDTH,
                  }}
                  valueProps={{
                    variant: 'body2',
                  }}
                  value={MoneyString(materialRow?.discountValue)}
                />
              </span>
            </Tooltip>

            <Tooltip
              title={
                <>{`${t('project:sales-price')}: ${MoneyString(
                  materialRow?.salesPrice,
                  10
                )}`}</>
              }
            >
              <span style={{ textAlign: 'right' }}>
                <ValueLabel
                  valueLabelRootStyles={{
                    width: VALUE_LABEL_WIDTH,
                  }}
                  valueProps={{
                    variant: 'body2',
                  }}
                  value={MoneyString(materialRow?.salesPrice)}
                />
              </span>
            </Tooltip>
          </div>
        </li>
      )}

      {workingStepRows
        ?.filter((x) =>
          !workingStepFilter.active
            ? true
            : workingStepFilter.filter.enabledFilters.includes(
                x.workingStep.primaryWorkingStep
              )
        )
        .map((x, i) => (
          <li
            key={`${x.workingStep.resource.id}-${i}-${x.workingStep?.key}`}
            className={classNames(classes.row, {
              [classes.highlight]: x.workingStep?.isPrimaryWorkingStep,
              [classes.sharedCosts]: x.costsAreShared,
            })}
          >
            <div
              className={classNames(classes.header, {
                [classes.secondaryWorkingStep]:
                  !x.workingStep.isPrimaryWorkingStep,
              })}
            >
              <WorkingStepIconSvg
                workingStepType={x.workingStep.primaryWorkingStep}
                attributes={{
                  width: '24px',
                }}
              />
              <div className={classes.resourceLabel}>
                <Typography variant="body1" component="p">
                  {x.workingStep.resource?.name}
                </Typography>
                <div className={classes.resourceDescription}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p"
                  >
                    {getLocalizedWorkingStep(x.workingStep.primaryWorkingStep)}{' '}
                    {x.workingStep.primaryWorkingStep !==
                      x.workingStep.secondaryWorkingStep &&
                      ` - ${getLocalizedWorkingStep(
                        x.workingStep.secondaryWorkingStep
                      )}`}
                    {x.costsAreShared && ` - ${t('project:costs-are-shared')}`}
                  </Typography>
                  <div style={{ alignSelf: 'flex-start' }}>
                    {x.isBatchIndependent ? (
                      <Tooltip
                        placement={'top'}
                        arrow
                        title={t(
                          'common:estimation-batch-independent',
                          'estimation and additional costs are quantity independent'
                        )}
                      >
                        <InfoOverIcon width={16} />
                      </Tooltip>
                    ) : null}
                  </div>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p"
                  >
                    {MoneyPerQuantityString(x.rate)}
                  </Typography>
                </div>
              </div>
              <IconButton
                size="small"
                onClick={() =>
                  downloadCostDriverCalculators(
                    props.bomItemId,
                    x.workingStep.primaryWorkingStep,
                    x.workingStep.secondaryWorkingStep
                  )
                }
              >
                <DownloadIcon width={'21px'} />
              </IconButton>
            </div>
            <div className={classes.values}>
              <ProdTimeInput
                bomItemId={props.bomItemId}
                priceRow={x}
                priceScope={props.financialUnit}
                handleResetProductionTime={props.handleResetProductionTime}
                handleSaveManuallySetProductionTime={
                  props.handleSaveProductionTime
                }
                loading={props.loading}
              />
              <Tooltip
                title={
                  <>{`${t('project:costs-prod')}: ${MoneyString(
                    x.productionTimeCosts,
                    10
                  )}`}</>
                }
              >
                <span style={{ textAlign: 'right' }}>
                  <ValueLabel
                    valueLabelRootStyles={{
                      width: VALUE_LABEL_WIDTH,
                    }}
                    valueProps={{
                      variant: 'body2',
                    }}
                    value={MoneyString(x?.productionTimeCosts)}
                  />
                </span>
              </Tooltip>
              <AdditionalProdCostsInput
                bomItemId={props.bomItemId}
                priceRow={x}
                priceScope={props.financialUnit}
                handleResetAdditionalCosts={props.handleResetAdditionalCosts}
                handleManualSaveAdditionalCosts={
                  props.handleSaveAdditionalCostPrice
                }
                loading={props.loading}
                defaultMoneyObject={x.costPrice}
              />
              <Tooltip
                title={
                  <>{`${t('project:costs-total')}: ${MoneyString(
                    x.costPrice,
                    10
                  )}`}</>
                }
              >
                <span style={{ textAlign: 'right' }}>
                  <ValueLabel
                    valueLabelRootStyles={{
                      width: VALUE_LABEL_WIDTH,
                    }}
                    valueProps={{
                      variant: 'body2',
                    }}
                    // label={'discount/surcharge'}
                    value={MoneyString(x?.costPrice)}
                  />
                </span>
              </Tooltip>
              <Tooltip
                title={
                  <>{`${t('common:surcharge')}: ${MoneyString(
                    x.surchargeValue,
                    10
                  )}`}</>
                }
              >
                <span style={{ textAlign: 'right', position: 'relative' }}>
                  <ValueLabel
                    valueLabelRootStyles={{
                      width: VALUE_LABEL_WIDTH,
                    }}
                    valueProps={{
                      variant: 'body2',
                    }}
                    // label={'discount/surcharge'}
                    value={MoneyString(x?.surchargeValue)}
                  />
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    component="div"
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: '-1.25em',
                    }}
                  >
                    {QuantityString(x?.surchargeRatio)}
                  </Typography>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  <>{`${t('common:discount')}: ${MoneyString(
                    x.discountValue,
                    10
                  )}`}</>
                }
              >
                <span style={{ textAlign: 'right', position: 'relative' }}>
                  <ValueLabel
                    valueLabelRootStyles={{
                      width: VALUE_LABEL_WIDTH,
                    }}
                    valueProps={{
                      variant: 'body2',
                    }}
                    // label={'discount/discount'}
                    value={MoneyString(x?.discountValue)}
                  />
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    component="div"
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: '-1.25em',
                    }}
                  >
                    {QuantityString(x?.discountRatio)}
                  </Typography>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  <>{`${t('project:sales-price')}: ${MoneyString(
                    x.salesPrice,
                    10
                  )}`}</>
                }
              >
                <span style={{ textAlign: 'right' }}>
                  <ValueLabel
                    valueLabelRootStyles={{
                      width: VALUE_LABEL_WIDTH,
                    }}
                    valueProps={{
                      variant: 'body2',
                    }}
                    // label={'sales price'}
                    value={MoneyString(x?.salesPrice)}
                  />
                </span>
              </Tooltip>
            </div>
          </li>
        ))}
      {/* <Typography variant="body2">total</Typography> */}
      <Divider />
      <li>
        <div className={classes.values} style={{ alignItems: 'stretch' }}>
          <ValueLabel
            label={'prod time'}
            labelTranslationKey={'project:prod-time'}
            value={TimeString(totalRow?.productionTime)}
            valueLabelRootStyles={{
              // whiteSpace: 'pre-line',
              width: VALUE_LABEL_WIDTH,
            }}
          />
          <ValueLabel
            label={'costs [prod time]'}
            labelTranslationKey={'project:costs-prod'}
            value={MoneyString(totalRow?.productionTimeCosts)}
            valueLabelRootStyles={{
              whiteSpace: 'pre-line',
              width: VALUE_LABEL_WIDTH,
            }}
          />
          <ValueLabel
            label={'costs [additional]'}
            labelTranslationKey={'project:costs-additional'}
            value={MoneyString(totalRow?.additionalProductionCosts)}
            valueLabelRootStyles={{
              whiteSpace: 'pre-line',
              width: VALUE_LABEL_WIDTH,
            }}
          />
          <ValueLabel
            label={'costs [total]'}
            labelTranslationKey={'project:costs-total'}
            value={MoneyString(totalRow?.costPrice)}
            valueLabelRootStyles={{
              whiteSpace: 'pre-line',
              width: VALUE_LABEL_WIDTH,
            }}
          />
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <ValueLabel
              fullWidth
              label={'surcharge'}
              labelTranslationKey={'common:surcharge'}
              value={MoneyString(totalRow?.surchargeValue)}
              valueLabelRootStyles={{
                whiteSpace: 'pre-line',
                width: VALUE_LABEL_WIDTH,
              }}
            />
            <Typography
              color="textSecondary"
              variant="caption"
              style={{ position: 'absolute', bottom: '-1.25em', right: 0 }}
            >
              {QuantityString(totalRow?.surchargeRatio)}
            </Typography>
          </div>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <ValueLabel
              fullWidth
              label={'discount'}
              labelTranslationKey={'common:discount'}
              value={MoneyString(totalRow?.discountValue)}
              valueLabelRootStyles={{
                whiteSpace: 'pre-line',
                width: VALUE_LABEL_WIDTH,
              }}
            />
            <Typography
              color="textSecondary"
              variant="caption"
              style={{ position: 'absolute', bottom: '-1.25em', right: 0 }}
            >
              {QuantityString(totalRow?.discountRatio)}
            </Typography>
          </div>

          <ValueLabel
            label={'sales price'}
            labelTranslationKey={'project:sales-price'}
            value={MoneyString(totalRow?.salesPrice)}
            valueLabelRootStyles={{
              whiteSpace: 'pre-line',
              width: VALUE_LABEL_WIDTH,
            }}
          />
        </div>
      </li>
    </ul>
  )
}
